import { createServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { requireAdmin } from "@/lib/admin/require-admin.server";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { dbError } from "@/lib/db";
import {
  syncProducts,
  syncStock,
  type ProductSyncResult,
  type StockSyncResult,
} from "@/lib/erp/tiny-sync.server";
import { pushOrderToTiny } from "@/lib/erp/order-sync.server";
import { pushProductToTiny } from "@/lib/erp/product-sync.server";

export type { ProductSyncResult, StockSyncResult };

export interface TinyConnectionStatus {
  connected: boolean;
  expiresAt: string | null;
  updatedAt: string | null;
}

export interface FailedTinySyncOrder {
  id: string;
  order_number: number;
  customer_name: string;
  tiny_sync_failed_at: string;
}

export interface FailedTinySyncProduct {
  id: string;
  name: string;
  sku: string | null;
  tiny_sync_failed_at: string;
}

// Handlers extraídos em funções simples: createServerFn lança "No Start
// context found" quando chamada fora de uma requisição real, então testar a
// const exportada diretamente quebra — mesmo motivo documentado em
// addresses.functions.ts.

// Nunca seleciona access_token/refresh_token — só metadados cruzam para o
// cliente, os valores sensíveis ficam só no servidor.
export async function fetchTinyConnectionStatus(): Promise<TinyConnectionStatus> {
  const { data, error } = await supabaseAdmin
    .from("erp_tiny_tokens")
    .select("expires_at, updated_at")
    .eq("id", true)
    .maybeSingle();
  if (error) throw dbError(error, "src/lib/admin/erp.functions.ts");
  return {
    connected: !!data,
    expiresAt: data?.expires_at ?? null,
    updatedAt: data?.updated_at ?? null,
  };
}

// Filtra no servidor em vez de buscar todos os pedidos e filtrar no
// cliente (o painel do Tiny só precisa dos pedidos com falha, não da lista
// completa que a página de pedidos do admin já busca separadamente).
export async function listFailedTinySyncOrders(): Promise<FailedTinySyncOrder[]> {
  const { data, error } = await supabaseAdmin
    .from("orders")
    .select("id, order_number, customer_name, tiny_sync_failed_at")
    .not("tiny_sync_failed_at", "is", null)
    .order("tiny_sync_failed_at", { ascending: false })
    .limit(100);
  if (error) throw dbError(error, "src/lib/admin/erp.functions.ts");
  return (data ?? []) as FailedTinySyncOrder[];
}

// Mesmo raciocínio de listFailedTinySyncOrders: filtra no servidor, só o
// necessário para o painel de reconciliação do Tiny.
export async function listFailedTinySyncProducts(): Promise<FailedTinySyncProduct[]> {
  const { data, error } = await supabaseAdmin
    .from("products")
    .select("id, name, sku, tiny_sync_failed_at")
    .not("tiny_sync_failed_at", "is", null)
    .order("tiny_sync_failed_at", { ascending: false })
    .limit(100);
  if (error) throw dbError(error, "src/lib/admin/erp.functions.ts");
  return (data ?? []) as FailedTinySyncProduct[];
}

export async function runProductSync(): Promise<ProductSyncResult> {
  try {
    return await syncProducts();
  } catch (error) {
    throw dbError(error, "src/lib/admin/erp.functions.ts");
  }
}

export async function runStockSync(): Promise<StockSyncResult> {
  try {
    return await syncStock();
  } catch (error) {
    throw dbError(error, "src/lib/admin/erp.functions.ts");
  }
}

// pushOrderToTiny retorna silenciosamente (sem lançar) quando algum item do
// pedido ainda não tem tiny_id mapeado — por isso não basta checar a
// ausência de erro, é preciso confirmar que tiny_pedido_id foi preenchido
// antes de limpar tiny_sync_failed_at.
export async function retryOrderSync(orderId: string): Promise<void> {
  try {
    await pushOrderToTiny(orderId);
  } catch (error) {
    const { error: updateError } = await supabaseAdmin
      .from("orders")
      .update({ tiny_sync_failed_at: new Date().toISOString() })
      .eq("id", orderId);
    if (updateError) throw dbError(updateError, "src/lib/admin/erp.functions.ts");
    throw error;
  }

  const { data: order, error: orderError } = await supabaseAdmin
    .from("orders")
    .select("tiny_pedido_id")
    .eq("id", orderId)
    .maybeSingle();
  if (orderError) throw dbError(orderError, "src/lib/admin/erp.functions.ts");

  if (!order?.tiny_pedido_id) {
    const { error: updateError } = await supabaseAdmin
      .from("orders")
      .update({ tiny_sync_failed_at: new Date().toISOString() })
      .eq("id", orderId);
    if (updateError) throw dbError(updateError, "src/lib/admin/erp.functions.ts");
    throw new Error(
      'Pedido tem item sem tiny_id vinculado. Rode "Vincular produtos" e tente novamente.',
    );
  }

  const { error: clearError } = await supabaseAdmin
    .from("orders")
    .update({ tiny_sync_failed_at: null })
    .eq("id", orderId);
  if (clearError) throw dbError(clearError, "src/lib/admin/erp.functions.ts");
}

// pushProductToTiny já limpa tiny_sync_failed_at quando dá certo (ver
// product-sync.server.ts), então aqui só resta marcar a falha de novo se a
// tentativa não der certo.
export async function retryProductSync(productId: string): Promise<void> {
  try {
    await pushProductToTiny(productId);
  } catch (error) {
    const { error: updateError } = await supabaseAdmin
      .from("products")
      .update({ tiny_sync_failed_at: new Date().toISOString() })
      .eq("id", productId);
    if (updateError) throw dbError(updateError, "src/lib/admin/erp.functions.ts");
    throw error;
  }
}

export const adminTinyConnectionStatus = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .handler(() => fetchTinyConnectionStatus());

export const adminFailedTinySyncOrders = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .handler(() => listFailedTinySyncOrders());

export const adminFailedTinySyncProducts = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .handler(() => listFailedTinySyncProducts());

export const adminTinySyncProducts = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .handler(() => runProductSync());

export const adminTinySyncStock = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .handler(() => runStockSync());

export const adminRetryTinyOrderSync = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((data: { orderId: string }) => z.object({ orderId: z.string().uuid() }).parse(data))
  .handler(async ({ data }) => retryOrderSync(data.orderId));

export const adminRetryTinyProductSync = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((data: { productId: string }) =>
    z.object({ productId: z.string().uuid() }).parse(data),
  )
  .handler(async ({ data }) => retryProductSync(data.productId));

// ---- Query options (client) ----

export const adminTinyConnectionStatusQuery = () =>
  queryOptions({
    queryKey: ["admin", "tiny", "connection-status"],
    queryFn: () => adminTinyConnectionStatus(),
  });

export const adminFailedTinySyncOrdersQuery = () =>
  queryOptions({
    queryKey: ["admin", "tiny", "failed-orders"],
    queryFn: () => adminFailedTinySyncOrders(),
  });

export const adminFailedTinySyncProductsQuery = () =>
  queryOptions({
    queryKey: ["admin", "tiny", "failed-products"],
    queryFn: () => adminFailedTinySyncProducts(),
  });
